/*
 * rheincool — kW-Rechner (kW-Rechner-Handoff.md)
 * kW = roomSize × W/m² × Decke × Sonne × Dämmung, auf Gerätestufen gerundet.
 * Alle Faktoren kommen aus der Section-Konfiguration ([PLATZHALTER —
 * fachlich validieren]), zentrale Konstanten aus #rc-constants.
 * Ergebnis wird als Store-Key 'kw' publiziert (article-store.js).
 */
(() => {
  'use strict';
  if (customElements.get('rc-kw-calculator')) return;

  /* Gerätestufen laut Handoff — kleinste Stufe, die den Bedarf deckt */
  const TIERS = [2.0, 2.5, 3.5, 5.0, 6.0];
  const LEISTUNG_ALIAS = {
    '2.0': '2-0-kw',
    '2.5': '2-5-kw',
    '3.5': '3-5-kw',
    '5.0': '5-0-kw',
  };

  class RcKwCalculator extends window.RcCalculator {
    init() {
      this.storeKey = 'kw';
      this.trackPrefix = 'calculator';
      this.lastSignature = null;

      this.els = {
        number: this.querySelector('[data-kw-number]'),
        unit: this.querySelector('[data-kw-unit]'),
        explain: this.querySelector('[data-kw-explain]'),
        roomLabel: this.querySelector('[data-calc-label-value="room"]'),
        stickyValue: this.querySelector('[data-kw-sticky-value]'),
        ctas: Array.from(this.querySelectorAll('[data-kw-cta]')),
      };

      this.els.ctas.forEach((cta) => {
        cta.addEventListener('click', () => {
          const result = window.rcStore?.get('kw');
          this.track('calculator_cta_click', {
            kw: result ? result.kw : null,
            multisplit: result ? result.multisplit : false,
          });
        });
      });
    }

    fill(template, values) {
      return Object.keys(values).reduce(
        (text, key) => text.split(`[${key}]`).join(values[key]),
        template || ''
      );
    }

    compute() {
      const room = this.fieldValue('room');
      if (Number.isNaN(room) || room <= 0) return null;
      const c = this.config;

      const ceilingKey = this.segmentValue('ceiling') || 'normal';
      const sunKey = this.segmentValue('sun') || 'normal';
      const insulationKey = this.segmentValue('insulation') || 'altbau';

      const ceiling = ceilingKey === 'high' ? c.factorCeiling : 1;
      const sun =
        { wenig: c.factorSunLow, normal: c.factorSunNormal, dg_sued: c.factorSunHigh }[sunKey] || 1;
      const insulation = insulationKey === 'neubau' ? c.factorInsNew : c.factorInsOld;

      const kwNeed = (room * c.wpm2 * ceiling * sun * insulation) / 1000;
      const kw = TIERS.find((tier) => tier >= kwNeed) || TIERS[TIERS.length - 1];
      const multisplit = room > c.multisplitThreshold || !LEISTUNG_ALIAS[kw.toFixed(1)];

      return {
        kw,
        multisplit,
        roomSize: room,
        ceiling: ceilingKey,
        sun: sunKey,
        insulation: insulationKey,
      };
    }

    ctaHref(result) {
      if (result.multisplit) return this.config.multisplitUrl;
      const base = this.config.ctaUrlBase || '';
      const separator = base.indexOf('?') === -1 ? '?' : '&';
      return `${base}${separator}leistung=${LEISTUNG_ALIAS[result.kw.toFixed(1)]}`;
    }

    renderResult(result) {
      const s = this.config.strings || {};
      const kwText = this.formatNumber(result.kw, 1);
      const roomText = this.formatNumber(result.roomSize, 0);
      const href = this.ctaHref(result);

      if (this.els.roomLabel) this.els.roomLabel.textContent = `${roomText} m²`;

      if (result.multisplit) {
        if (this.els.number) this.els.number.textContent = s.multisplitLabel;
        if (this.els.unit) this.els.unit.hidden = true;
        if (this.els.explain) this.els.explain.textContent = this.fill(s.multisplitExplain, { room: roomText });
        if (this.els.stickyValue) this.els.stickyValue.textContent = s.multisplitLabel;
        this.announce(this.fill(s.multisplitAnnounce, { room: roomText }));
      } else {
        if (this.els.number) this.els.number.textContent = kwText;
        if (this.els.unit) this.els.unit.hidden = false;
        if (this.els.explain) this.els.explain.textContent = this.fill(s.explain, { kw: kwText, room: roomText });
        if (this.els.stickyValue) this.els.stickyValue.textContent = `${kwText} kW`;
        this.announce(this.fill(s.announce, { kw: kwText, room: roomText }));
      }

      this.els.ctas.forEach((cta) => {
        cta.href = href;
        if (!cta.hasAttribute('data-kw-sticky-cta')) {
          cta.textContent = result.multisplit ? s.multisplitCta : this.fill(s.cta, { kw: kwText });
        } else if (result.multisplit) {
          cta.textContent = s.multisplitCta;
        } else {
          cta.textContent = s.ctaShort;
        }
      });

      const signature = result.multisplit ? 'multisplit' : String(result.kw);
      if (signature !== this.lastSignature) {
        this.lastSignature = signature;
        this.track('calculator_result', {
          kw: result.kw,
          roomSize: result.roomSize,
          ceiling: result.ceiling,
          sun: result.sun,
          insulation: result.insulation,
          multisplit: result.multisplit,
        });
      }
    }

    trackParams() {
      return { roomSize: this.fieldValue('room') };
    }
  }

  customElements.define('rc-kw-calculator', RcKwCalculator);
})();
